import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import DateRangePicker from "../components/DateRangePicker";
import SyncButtonWithTimestamp from '../components/SyncButtonWithTimestamp';
import EmptyState from '../components/EmptyState';
import TooltipUI from "../components/Tooltip";
import InfoIcon from "../components/InfoIcon";
import EngagementScoreChart from "../components/EngagementScoreChart";
import SentimentScoreChart from "../components/SentimentScoreChart";
import ClarityScoreChart from "../components/ClarityScoreChart";
import AISummaryWidget from "../components/AISummaryWidget";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine,
  ScatterChart, 
  Scatter, 
  Cell 
} from 'recharts';

// Import Slack data service functions
import {
  fetchOrganization,
  checkSlackConnection,
  fetchSlackChannels,
  calculateNetworkMetrics,
  getNetworkTimeSeriesData,
  generateNetworkData
} from "../utils/slackDataService";

// Import report service functions
import {
  fetchReport,
  calculateToneTimeData,
  calculateClarityTimeData,
} from "../utils/slackReportService";

import { shouldBypassSlackConnection } from "../utils/config";
import { getApiBaseUrl } from "../utils/apiConfig";

// Calculate collaboration health index using all available metrics
const calculateCollaborationHealthIndex = (
  engagementData, 
  sentimentData, 
  toneData, 
  clarityData, 
  networkMetrics
) => {
  try {
    // Calculate averages for each metric
    const avgEngagement = engagementData.length > 0 
      ? engagementData.reduce((sum, item) => sum + (item.score || 0), 0) / engagementData.length 
      : 0;
    
    // Sentiment scores from generateSentimentTimeData are in -1 to 1 range, convert to 0-100
    const avgSentiment = sentimentData.length > 0 
      ? sentimentData.reduce((sum, item) => {
          const score = item.score || 0;
          return sum + ((score + 1) / 2) * 100; // Convert -1 to 1 range to 0-100
        }, 0) / sentimentData.length
      : 0;
    
    const avgTone = toneData.length > 0 
      ? toneData.reduce((sum, item) => sum + (item.score || 0), 0) / toneData.length 
      : 0;
    
    const avgClarity = clarityData.length > 0 
      ? clarityData.reduce((sum, item) => sum + (item.score || 0), 0) / clarityData.length 
      : 0;
    
    // Network metrics (already 0-100 scale)
    const networkDensity = networkMetrics.networkDensity || 0;
    const siloIndex = 100 - (networkMetrics.siloIndex || 0); // Invert silo index (lower silo = better collaboration)
    const bottleneckIndex = 100 - (networkMetrics.bottleneckIndex || 0); // Invert bottleneck (lower bottleneck = better)
    const networkCentralization = networkMetrics.networkCentralization || 0;
    
    // Weighted calculation for collaboration health index
    const collaborationIndex = (
      avgEngagement * 0.25 +           // 25% - Team engagement
      avgSentiment * 0.15 +            // 15% - Emotional health
      avgTone * 0.15 +                 // 15% - Communication tone
      avgClarity * 0.15 +              // 15% - Communication clarity  
      networkDensity * 0.15 +          // 15% - Network connectivity
      siloIndex * 0.05 +               // 5% - Reduced information silos
      bottleneckIndex * 0.05 +         // 5% - Reduced bottlenecks
      networkCentralization * 0.05     // 5% - Network structure
    );
    
    return Math.round(Math.max(0, Math.min(100, collaborationIndex)));
  } catch (error) {
    console.error("Error calculating collaboration health index:", error);
    return 0; // Return 0 when no data available
  }
};

// Generate per-day collaboration health index chart data by joining all four metric arrays on date
const generateCollaborationChartData = (engagementData, sentimentData, toneData, clarityData, networkMetrics) => {
  const dateMap = {};

  engagementData.forEach(({ date, score }) => {
    if (!dateMap[date]) dateMap[date] = {};
    dateMap[date].engagement = score || 0;
  });
  sentimentData.forEach(({ date, score }) => {
    if (!dateMap[date]) dateMap[date] = {};
    dateMap[date].sentiment = ((score || 0) + 1) / 2 * 100; // -1..1 → 0..100
  });
  toneData.forEach(({ date, score }) => {
    if (!dateMap[date]) dateMap[date] = {};
    dateMap[date].tone = score || 0;
  });
  clarityData.forEach(({ date, score }) => {
    if (!dateMap[date]) dateMap[date] = {};
    dateMap[date].clarity = score || 0;
  });

  const networkDensity = networkMetrics?.networkDensity || 0;
  const siloIndex = 100 - (networkMetrics?.siloIndex || 0);
  const bottleneckIndex = 100 - (networkMetrics?.bottleneckIndex || 0);
  const networkCentralization = networkMetrics?.networkCentralization || 0;

  return Object.entries(dateMap)
    .sort(([a], [b]) => new Date(a) - new Date(b))
    .map(([date, m]) => {
      const engagement = m.engagement ?? 0;
      const sentiment = m.sentiment ?? 0;
      const tone = m.tone ?? 0;
      const clarity = m.clarity ?? 0;
      const score = Math.round(Math.max(0, Math.min(100,
        engagement * 0.25 +
        sentiment  * 0.15 +
        tone       * 0.15 +
        clarity    * 0.15 +
        networkDensity        * 0.15 +
        siloIndex             * 0.05 +
        bottleneckIndex       * 0.05 +
        networkCentralization * 0.05
      )));
      return { date, score };
    });
};

// Calculate engagement time data from report data  
const calculateEngagementTimeData = (reportData, startDate, endDate) => {
  if (!reportData?.engagement?.engagementScore) {
    console.log('No engagement data available in report');
    return [];
  }

  const filterStartDate = startDate ? new Date(startDate) : null;
  const filterEndDate = endDate ? new Date(endDate) : null;

  const messageCountsByDate = {};
  if (reportData.engagement.channelEngagement) {
    Object.values(reportData.engagement.channelEngagement).forEach((channelDates) => {
      Object.entries(channelDates).forEach(([dateStr, dayData]) => {
        const messageCount = dayData.numberOfMessages || 0;
        messageCountsByDate[dateStr] = (messageCountsByDate[dateStr] || 0) + messageCount;
      });
    });
  }

  const timeData = [];

  Object.entries(reportData.engagement.engagementScore).forEach(([dateStr, score]) => {
    const datePart = dateStr.split(',')[0].trim();
    // Parse date parts manually to avoid timezone-driven date shifts from toISOString()
    const [mName, mDay, mYear] = datePart.split(' ');
    const MONTH_NUMS = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
    const monthIdx = MONTH_NUMS[mName];
    const dayNum = parseInt(mDay);
    const yearNum = parseInt(mYear);
    const messageDate = new Date(yearNum, monthIdx, dayNum); // local midnight, safe for comparison
    const isoDate = `${yearNum}-${String(monthIdx + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    
    // Check if this date is in the filter range
    if (filterStartDate && messageDate < filterStartDate) return;
    if (filterEndDate && messageDate > filterEndDate) return;
    
    timeData.push({
      date: isoDate,
      score: typeof score === 'number' ? Math.round(score * 100) : 0, // Convert 0-1 to 0-100
      messageCount: messageCountsByDate[dateStr] || 0,
    });
  });
  
  // Sort by date
  timeData.sort((a, b) => new Date(a.date) - new Date(b.date));
  
  console.log('Processed engagement time data:', timeData.length, 'entries');
  return timeData;
};

// Custom chart component for index metrics 
const IndexChart = ({ title, data, score, loading, height = 340 }) => {
  // Use consistent badge logic for collaboration/communication indexes (80/60/40/20 thresholds like other metrics)
  const getIndexStatus = (score) => {
    if (score >= 80) return "Very high";
    if (score >= 60) return "High";
    if (score >= 40) return "Medium";
    if (score >= 20) return "Low";
    return "Very low";
  };
  
  const getIndexColor = (score) => {
    if (score >= 80) return "#14B882";  // Green
    if (score >= 60) return "#14B882";  // Green
    if (score >= 40) return "#F59E0B";  // Yellow
    if (score >= 20) return "#E50026";  // Red
    return "#E50026";  // Red
  };
  
  const scoreStatus = getIndexStatus(score);
  const scoreColor = getIndexColor(score);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return `${date.toLocaleString('default', { month: 'short' })} ${day}`;
  };
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const [year, month, day] = label.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const formattedDate = date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric'
      });
      
      return (
        <div className="bg-white-f p-3 border border-grey-20 rounded-md shadow-subtle">
          <p className="text-sm font-medium">{formattedDate}</p>
          <p className="text-sm text-grey-60">
            {title}: <span className="font-medium" style={{color: scoreColor}}>{Math.round(payload[0].value)}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const titleHelp = (() => {
    switch (title) {
      case "Collaboration health index":
        return "A composite score (0–100) combining engagement, sentiment, tone, clarity, and collaboration network structure.";
      default:
        return "A daily index score (0–100) for the selected period.";
    }
  })();
  
  return (
    <div className="bg-white-f p-5 pl-0 rounded-lg border border-grey-20 shadow-subtle" style={{ height }}>
      <div className="mb-4 pl-5 flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-normal text-black-0">{title}</h3>
          <TooltipUI content={titleHelp} placement="bottom" align="start">
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <div className="flex items-center gap-2">
          <p className="font-medium text-[18px] text-black-0">
            {score}%
          </p>
          <span className={`px-2 py-1 rounded-md text-xs font-normal ${getIndexBadgeColor(scoreStatus)}`}>
            {scoreStatus}
          </span>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin"></div>
        </div>
      ) : data.length === 0 ? (
        <div className="flex justify-center items-center h-32">
          <p className="text-sm text-grey-60">No data available for the selected period</p>
        </div>
      ) : (
        <div style={{ height: height - 120 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate} 
                tick={{ fontSize: 12 }}
                tickMargin={10}
                stroke="#ABABBA"
              />
              <YAxis 
                domain={[0, 100]} 
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 12 }}
                tickMargin={10}
                stroke="#ABABBA"
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="linear" 
                dataKey="score" 
                stroke="#31048B"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 5, fill: "#31048B" }}
                isAnimationActive={true}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// Helper function for index badge colors
const getIndexBadgeColor = (level) => {
  switch (level.toLowerCase()) {
    case 'high':
    case 'very high':
      return 'bg-[#DAFBEB] text-black-0';
    case 'medium':
      return 'bg-[#FBF0DA] text-black-0';
    case 'low':
    case 'very low':
      return 'bg-[#FBDADA] text-black-0';
    default:
      return 'bg-grey-20 text-grey-60';
  }
};

const DashboardWidget = ({ onViewMore, children }) => {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onViewMore}
        className="absolute top-4 right-4 z-10 text-xs text-grey-60 hover:text-brand hover:underline"
      >
        View more
      </button>
      {children}
    </div>
  );
};

const getMetricLevel = (score) => {
  if (score >= 80) return "Very high";
  if (score >= 60) return "High";
  if (score >= 40) return "Medium";
  if (score >= 20) return "Low";
  return "Very low";
};

const formatIsoDateShort = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return `${date.toLocaleString("default", { month: "short" })} ${day}`;
};

/** Matches Network Analysis network density chart tooltip (date + app chrome). */
const NetworkDensityChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length && label) {
    const [year, month, day] = String(label).split("-").map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    const formattedDate = date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

    return (
      <div className="bg-white-f p-3 border border-grey-20 rounded-md shadow-subtle">
        <p className="text-sm font-medium text-black-0">{formattedDate}</p>
        <p className="text-sm text-grey-60">
          Network density:{" "}
          <span className="font-medium text-black-0">
            {Math.round(payload[0].value)}%
          </span>
        </p>
      </div>
    );
  }
  return null;
};

const normalizeSentimentPercents = (dist) => {
  const clamp01 = (v) => {
    if (typeof v !== "number" || Number.isNaN(v)) return 0;
    return Math.max(0, Math.min(1, v));
  };

  const pRaw = clamp01(dist?.positive);
  const nRaw = clamp01(dist?.negative);
  const uRaw = typeof dist?.neutral === "number" ? clamp01(dist.neutral) : null;
  const uInferred = uRaw === null ? clamp01(1 - pRaw - nRaw) : uRaw;

  const total = pRaw + uInferred + nRaw;
  if (total <= 0) return { positive: 0, neutral: 0, negative: 0, score: 0 };

  const parts = [
    { key: "positive", value: (pRaw / total) * 100 },
    { key: "neutral", value: (uInferred / total) * 100 },
    { key: "negative", value: (nRaw / total) * 100 },
  ];

  parts.forEach((p) => {
    p.floor = Math.floor(p.value);
    p.frac = p.value - p.floor;
  });

  let remaining = 100 - parts.reduce((s, p) => s + p.floor, 0);
  parts
    .sort((a, b) => b.frac - a.frac)
    .forEach((p) => {
      if (remaining <= 0) return;
      p.floor += 1;
      remaining -= 1;
    });

  const result = {};
  parts.forEach((p) => {
    result[p.key] = p.floor;
  });

  // -1..1 sentiment score used by dashboard indexes
  result.score = parseFloat(((pRaw - nRaw) / total).toFixed(2));
  return result;
};

// Teamwork Temperature Chart component (Figma tooltip: node 8110:8)
const TeamworkTemperatureChart = ({ data, loading, height = 360, memberMap = null }) => {
  // Helper – classify score
  const getIndexStatus = (score) => {
    if (score >= 80) return { text: 'Very high', color: 'bg-[#DAFBEB] text-black-0' };
    if (score >= 60) return { text: 'High', color: 'bg-[#DAFBEB] text-black-0' };
    if (score >= 40) return { text: 'Medium', color: 'bg-[#FBF0DA] text-black-0' };
    if (score >= 20) return { text: 'Low', color: 'bg-[#FBDADA] text-black-0' };
    return { text: 'Very low', color: 'bg-[#FBDADA] text-black-0' };
  };

  // Formula: 70% weight on degree centrality, 30% on publicness (1 - publicIndex)
  const overallScore = React.useMemo(() => {
    if (!data || data.length === 0) return 0;
    const total = data.reduce((sum, n) => {
      const centrality = typeof n.degreeCentrality === 'number' ? n.degreeCentrality : 0;
      const publicness = 1 - (typeof n.publicIndex === 'number' ? n.publicIndex : 0);
      return sum + (0.7 * centrality + 0.3 * publicness);
    }, 0);
    return Math.round((total / data.length) * 100);
  }, [data]);

  // Color palette function (matching NetworkGraph)
  const getColorFromPalette = (publicIndex) => {
    const privatePercentage = Math.max(0, Math.min(100, publicIndex * 100));
    
    const colorPalette = [
      { range: [0, 5], color: '#36098A' },
      { range: [6, 10], color: '#401186' },
      { range: [11, 15], color: '#491983' },
      { range: [16, 20], color: '#522180' },
      { range: [21, 25], color: '#5C297D' },
      { range: [26, 30], color: '#653279' },
      { range: [31, 35], color: '#6E3A76' },
      { range: [36, 40], color: '#784272' },
      { range: [41, 45], color: '#814A6F' },
      { range: [46, 50], color: '#8A526C' },
      { range: [51, 55], color: '#935A68' },
      { range: [56, 60], color: '#9D6365' },
      { range: [61, 65], color: '#A66B61' },
      { range: [66, 70], color: '#AF735E' },
      { range: [71, 75], color: '#B87B5B' },
      { range: [76, 80], color: '#C28357' },
      { range: [81, 85], color: '#CB8B54' },
      { range: [86, 90], color: '#D49350' },
      { range: [91, 95], color: '#DE9C4D' },
      { range: [96, 100], color: '#E7A449' }
    ];
    
    for (const { range, color } of colorPalette) {
      if (privatePercentage >= range[0] && privatePercentage <= range[1]) {
        return color;
      }
    }
    return '#36098A';
  };

  if (loading) {
    return (
      <div className="bg-white-f p-5 pl-0 rounded-lg border border-grey-20 shadow-subtle" style={{ height }}>
        <div className="mb-4 pl-5 flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <h3 className="text-sm font-normal text-black-0">Teamwork temperature</h3>
            <TooltipUI
              content="Measures how balanced collaboration is between public and private channels, and how centralized the network is. Higher is healthier teamwork."
              placement="bottom"
              align="start"
            >
              <span className="inline-flex cursor-help">
                <InfoIcon />
              </span>
            </TooltipUI>
          </div>
        </div>
        <div className="flex justify-center items-center h-32">
          <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white-f p-5 pl-0 rounded-lg border border-grey-20 shadow-subtle" style={{ height }}>
        <div className="mb-4 pl-5 flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <h3 className="text-sm font-normal text-black-0">Teamwork temperature</h3>
            <TooltipUI
              content="Measures how balanced collaboration is between public and private channels, and how centralized the network is. Higher is healthier teamwork."
              placement="bottom"
              align="start"
            >
              <span className="inline-flex cursor-help">
                <InfoIcon />
              </span>
            </TooltipUI>
          </div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-[18px] text-black-0">0%</p>
            <span className="px-2 py-1 rounded-md text-xs font-normal bg-grey-20 text-grey-60">No data</span>
          </div>
        </div>
        <div className="flex justify-center items-center h-32">
          <p className="text-sm text-grey-60">No network data available</p>
        </div>
      </div>
    );
  }

  // Normalize helper for ratios that may come as 0–1 or 0–100
  const normalizeRatio = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value !== 'number' || Number.isNaN(value)) return null;
    let v = value;
    if (v > 1) {
      v = v / 100; // Treat >1 as percentage
    }
    if (v < 0) v = 0;
    if (v > 1) v = 1;
    return v;
  };

  const chartData = data
    .map((node) => {
      const normPublicIndex = normalizeRatio(node.publicIndex);
      const normCentrality = normalizeRatio(node.degreeCentrality);

      if (normPublicIndex === null || normCentrality === null) {
        return null;
      }

      const privatePercent = Math.round(normPublicIndex * 100);

      return {
        id: node.id,
        name: node.name || null,
        title: node.title || null,
        x: normPublicIndex,          // 0 = public‑heavy, 1 = private‑heavy
        y: normCentrality,           // 0 = low centrality, 1 = high
        color: getColorFromPalette(normPublicIndex),
        privatePercent,
        pubMsgs:
          node.channelActivity?.publicMessages ??
          node.channelActivity?.publicInteractions ??
          0,
        privMsgs:
          node.channelActivity?.privateMessages ??
          node.channelActivity?.privateInteractions ??
          0,
      };
    })
    .filter(Boolean);

  const badge = getIndexStatus(overallScore);

  const metricBadge = (pct) => {
    const p = Math.max(0, Math.min(100, Math.round(pct)));
    if (p === 0) return { label: "None", className: "bg-grey-20" };
    if (p < 34) return { label: "Low", className: "bg-[#fbdada]" };
    if (p < 67) return { label: "Medium", className: "bg-[#fbf0da]" };
    return { label: "High", className: "bg-[#dafbeb]" };
  };

  const siloBadge = (siloPct) => {
    const p = Math.max(0, Math.min(100, Math.round(siloPct)));
    if (p <= 33) return { label: "Low", className: "bg-[#dafbeb]" };
    if (p <= 66) return { label: "Medium", className: "bg-[#fbf0da]" };
    return { label: "High", className: "bg-[#fbdada]" };
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload || {};
    const { privatePercent, y, id, name, title, pubMsgs, privMsgs } = row;

    if (privatePercent === undefined) return null;

    const connectivityPct = Math.round((typeof y === "number" ? y : 0) * 100);
    const connBadge = metricBadge(connectivityPct);
    const siloB = siloBadge(privatePercent);
    const memberInfo = id
      ? (memberMap?.get?.(id) ?? memberMap?.get?.(String(id)))
      : null;
    const roleTitle =
      title ||
      memberInfo?.title ||
      name ||
      memberInfo?.name ||
      "Member";

    return (
      <div className="bg-white-f border border-grey-30 rounded-lg shadow-[0px_1px_5px_0px_rgba(28,27,34,0.1)] overflow-hidden w-[260px] max-w-[260px] font-schibsted text-black-0">
        <div className="border-b border-grey-20 px-4 py-3">
          <p className="font-semibold text-base leading-[22px] tracking-[-0.5px] whitespace-nowrap">
            {roleTitle}
          </p>
        </div>
        <div className="flex flex-col gap-1 px-4 py-3">
          <div className="flex gap-1 items-baseline h-6 w-full">
            <span className="flex-1 min-w-0 font-normal text-sm leading-[22px] tracking-[-0.5px]">
              Connectivity
            </span>
            <span className="font-medium text-sm leading-[22px] tracking-[-0.5px] whitespace-nowrap">
              {connectivityPct}%
            </span>
            <span
              className={`shrink-0 h-6 px-2 flex items-center justify-center rounded-md text-sm font-medium leading-[22px] ${connBadge.className}`}
            >
              {connBadge.label}
            </span>
          </div>
          <div className="flex gap-1 items-baseline h-6 w-full">
            <span className="flex-1 min-w-0 font-normal text-sm leading-[22px] tracking-[-0.5px]">
              Silo index
            </span>
            <span className="font-medium text-sm leading-[22px] tracking-[-0.5px] whitespace-nowrap">
              {privatePercent}%
            </span>
            <span
              className={`shrink-0 h-6 px-2 flex items-center justify-center rounded-md text-sm font-medium leading-[22px] ${siloB.className}`}
            >
              {siloB.label}
            </span>
          </div>
          <div className="flex h-6 items-baseline justify-between text-sm leading-[22px] tracking-[-0.5px] whitespace-nowrap">
            <span className="font-normal">Public channels</span>
            <span className="font-medium">{pubMsgs} messages</span>
          </div>
          <div className="flex h-6 items-baseline justify-between text-sm leading-[22px] tracking-[-0.5px] whitespace-nowrap">
            <span className="font-normal">Private channels</span>
            <span className="font-medium">{privMsgs} messages</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white-f p-5 pl-0 rounded-lg border border-grey-20 shadow-subtle" style={{ height }}>
      <div className="mb-4 pl-5 flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-normal text-black-0">Teamwork temperature</h3>
          <TooltipUI
            content="Measures how balanced collaboration is between public and private channels, and how centralized the network is. Higher is healthier teamwork."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <div className="flex items-center gap-2">
          <p className="font-medium text-[18px] text-black-0">{overallScore}%</p>
          <span className={`px-2 py-1 rounded-md text-xs font-normal ${badge.color}`}>{badge.text}</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height - 120}>
        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
          <XAxis 
            type="number" 
            dataKey="x" 
            domain={[0,1]} 
            ticks={[0, 0.25, 0.5, 0.75, 1]} 
            tick={{ fontSize: 12 }}
            tickMargin={10}
            stroke="#ABABBA"
            tickFormatter={(val) => {
              if (val === 0) return 'Public';
              if (val === 0.25) return 'More public';
              if (val === 0.5) return 'Balanced';
              if (val === 0.75) return 'More private';
              if (val === 1) return 'Private';
              return '';
            }}
          />
          <YAxis 
            type="number" 
            dataKey="y" 
            domain={[0,1]} 
            ticks={[0, 0.5, 1]} 
            tick={{ fontSize: 12 }}
            tickMargin={10}
            stroke="#ABABBA"
            tickFormatter={(val) => {
              if (val === 0) return 'Low';
              if (val === 0.5) return 'Medium';
              if (val === 1) return 'High';
              return '';
            }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
          <Scatter 
            name="Users" 
            data={chartData} 
            shape="circle"
            fill="#31048B"
            legendType="circle"
            isAnimationActive={true}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [slackConnected, setSlackConnected] = useState(false);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  
  // Initialize dateRange with "last30days" 
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 29);
    const endDate = new Date(today);
    return { startDate, endDate, range: "last30days" };
  });
  
  // Data states
  const [engagementScoreData, setEngagementScoreData] = useState([]);
  const [sentimentScoreData, setSentimentScoreData] = useState([]);
  const [toneScoreData, setToneScoreData] = useState([]);
  const [clarityScoreData, setClarityScoreData] = useState([]);
  const [networkMetrics, setNetworkMetrics] = useState({});
  
  // Add state for network graph
  const [networkGraph, setNetworkGraph] = useState({ nodes: [], links: [] });
  const [networkDensityData, setNetworkDensityData] = useState([]);
  const [memberMap, setMemberMap] = useState(null);
  
  // Calculated index states
  const [collaborationHealthIndex, setCollaborationHealthIndex] = useState(0);
  const [collaborationChartData, setCollaborationChartData] = useState([]);
  
  // AI insights summary state (cached on backend + sessionStorage)
  const [aiSummary, setAiSummary] = useState("");
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  
  // Individual metric scores for the digest table
  const [engagementScore, setEngagementScore] = useState(0);
  const [sentimentScore, setSentimentScore] = useState(0);
  const [toneScore, setToneScore] = useState(0);
  const [clarityScore, setClarityScore] = useState(0);
  
  // Check user session and load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      const userData = localStorage.getItem("user");
      if (userData && userData !== "undefined") {
        try {
          setLoading(true);
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          if (parsedUser.organizations && parsedUser.organizations.length > 0) {
            const firstOrgId = parsedUser.organizations[0].organizationId;
            const org = await fetchOrganization(firstOrgId);
            setOrganization(org);
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
          setError("Error loading user data. Please try again.");
        } finally {
          setLoading(false);
        }
      } else {
        navigate("/login");
      }
    };
    
    loadInitialData();
  }, [navigate]);

  // Slack directory (job titles for teamwork temperature tooltips)
  useEffect(() => {
    if (!organization) return;
    const orgId = organization._id || organization.id;
    if (!orgId) return;

    const loadDirectory = async () => {
      try {
        const token = localStorage.getItem("token");
        const apiUrl = getApiBaseUrl();
        const res = await fetch(`${apiUrl}/integrations/slack/directory/${orgId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.members && Array.isArray(data.members)) {
          const map = new Map();
          data.members.forEach((m) => map.set(m.slackUserId, m));
          setMemberMap(map);
        }
      } catch {
        /* ignore */
      }
    };
    loadDirectory();
  }, [organization]);
  
  // Check Slack connection and load data
  useEffect(() => {
    const loadSlackData = async () => {
      if (!organization) return;
      
      try {
        setLoading(true);
        setError(null);
        
        if (shouldBypassSlackConnection()) {
          console.log('Dashboard.jsx: Bypassing Slack check, setting slackConnected=true');
          setSlackConnected(true);
          const channels = await fetchSlackChannels(organization);
          setAvailableChannels(channels);
        } else {
          const isConnected = await checkSlackConnection(organization);
          setSlackConnected(isConnected);
          
          if (isConnected) {
            const channels = await fetchSlackChannels(organization);
            setAvailableChannels(channels);
          } else {
            setError("Slack is not connected. Please connect Slack in the Integrations page.");
          }
        }
      } catch (error) {
        console.error("Error loading Slack data:", error);
        if (!shouldBypassSlackConnection()) {
          setError("Error checking Slack connection. Please try again.");
        }
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };
    
    loadSlackData();
  }, [organization]);

  const toLocalIso = useCallback((d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  const fetchAiSummary = useCallback(async (org, range, { signal } = {}) => {
    try {
      if (!org || !range?.startDate || !range?.endDate) return;

      const orgId = org._id || org.id;
      if (!orgId) return;

      const cacheVersion = "v4";
      const start = new Date(range.startDate);
      const end = new Date(range.endDate);
      const startIso = toLocalIso(start);
      const endIso = toLocalIso(end);

      const cacheKey = `aiSummary:${cacheVersion}:${orgId}:${startIso}:${endIso}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed?.summaryText) {
            setAiSummary(parsed.summaryText);
            return;
          }
        } catch {
          // ignore malformed cache
        }
      }

      setAiSummaryLoading(true);
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      const res = await fetch(
        `${apiUrl}/analytics/summary/collaboration/${orgId}?startDate=${encodeURIComponent(startIso)}&endDate=${encodeURIComponent(endIso)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        }
      );
      if (!res.ok) throw new Error(`AI summary request failed (${res.status})`);
      const data = await res.json();
      const summaryText = typeof data?.summaryText === "string" ? data.summaryText : "";
      setAiSummary(summaryText);
      sessionStorage.setItem(cacheKey, JSON.stringify({ summaryText, createdAt: data?.createdAt || null }));
    } catch (e) {
      if (e.name === "AbortError") return; // Request was cancelled, ignore
      console.warn("Failed to load AI summary:", e);
      setAiSummary("No summary available for the selected period.");
    } finally {
      setAiSummaryLoading(false);
    }
  }, [toLocalIso]);

  // Single consolidated effect for loading dashboard data + AI summary.
  // Fires on initial ready state and whenever dateRange changes.
  useEffect(() => {
    if (!slackConnected || !organization || !initialLoadComplete) return;

    const abortController = new AbortController();
    const isInitialLoad = !engagementScoreData.length && !sentimentScoreData.length;

    loadDashboardData(isInitialLoad ? false : true);
    fetchAiSummary(organization, dateRange, { signal: abortController.signal });

    return () => abortController.abort();
    // eslint-disable-next-line -- loadDashboardData is intentionally omitted: it reads current state via closure and would cause infinite loops if included
  }, [dateRange, slackConnected, organization, initialLoadComplete]);
  
  // Callback to re-fetch all dashboard data after a sync
  const handleSyncComplete = useCallback(() => {
    loadDashboardData(true);
    fetchAiSummary(organization, dateRange);
  }, [organization, dateRange, fetchAiSummary]);

  // Load all dashboard data using the same approach as working pages
  const loadDashboardData = async (isFilterChange = true) => {
    if (!organization || !slackConnected) {
      console.log("Cannot load data: organization or slack not connected");
      return;
    }
    
    try {
      if (isFilterChange) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      setNoDataAvailable(false);
      
      console.log("Loading dashboard data with dateRange:", dateRange);
      
      // Ensure we have valid date objects
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      
      console.log(`Date range: ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`);
      
      const currentDateRange = {
        startDate: startDate,
        endDate: endDate,
        range: dateRange.range
      };
      
      // Load data using the same working functions as SentimentAnalysis page
      let engagementData = [];
      let sentimentData = [];
      let toneData = [];
      let clarityData = [];
      
      // Load other data from report (these work fine)
      const reportResult = await fetchReport(organization);
      if (reportResult && reportResult.success && reportResult.data) {
        console.log("Extracting data from report...");
        
        const reportData = reportResult.data.reportData;
        const startDateStr = startDate.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];

        // Sentiment time-series (normalized to sum to 100%)
        try {
          const distribution = reportData?.sentiment?.sentimentDistribution;
          if (distribution) {
            const MONTH_NUMS = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
            const timeData = [];
            Object.entries(distribution).forEach(([dateStr, dist]) => {
              const datePart = dateStr.split(",")[0].trim();
              const [mName, mDay, mYear] = datePart.split(" ");
              const monthIdx = MONTH_NUMS[mName];
              const dayNum = parseInt(mDay);
              const yearNum = parseInt(mYear);
              const dateObj = new Date(yearNum, monthIdx, dayNum);
              if (dateObj < startDate || dateObj > endDate) return;

              const isoDate = `${yearNum}-${String(monthIdx + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
              const normalized = normalizeSentimentPercents(dist);
              timeData.push({ date: isoDate, ...normalized });
            });
            timeData.sort((a, b) => new Date(a.date) - new Date(b.date));
            sentimentData = timeData;
          } else {
            sentimentData = [];
          }
        } catch (error) {
          console.warn("Failed to compute sentiment time series from report:", error);
          sentimentData = [];
        }
        
        // Calculate data from the database report data using the same functions as other pages
        engagementData = calculateEngagementTimeData(reportData, startDateStr, endDateStr);
        toneData = calculateToneTimeData(reportData, startDateStr, endDateStr);
        clarityData = calculateClarityTimeData(reportData, startDateStr, endDateStr);
        
        console.log("Data extracted from report:", {
          engagement: engagementData.length,
          sentiment: sentimentData.length,
          tone: toneData.length,
          clarity: clarityData.length,
        });
      }
      
      // Load network data in parallel (metrics, graph, time series)
      const [networkResult, networkGraphResult, densityResult] = await Promise.allSettled([
        calculateNetworkMetrics(organization, null, currentDateRange),
        generateNetworkData(organization, null, currentDateRange),
        getNetworkTimeSeriesData(organization, null, currentDateRange),
      ]);

      let networkData = {};
      if (networkResult.status === "fulfilled") {
        networkData = networkResult.value || {};
        console.log("Network metrics loaded successfully");
      } else {
        console.warn("Failed to load network metrics:", networkResult.reason);
      }

      let networkGraphData = { nodes: [], links: [] };
      if (networkGraphResult.status === "fulfilled" && networkGraphResult.value) {
        networkGraphData = networkGraphResult.value;
        console.log("Network graph loaded:", networkGraphData.nodes.length, "nodes");
      } else {
        console.warn("Failed to load network graph:", networkGraphResult.reason);
      }

      let densitySeries = [];
      if (densityResult.status === "fulfilled") {
        densitySeries = Array.isArray(densityResult.value?.networkDensityData) ? densityResult.value.networkDensityData : [];
      } else {
        console.warn("Failed to load network time series:", densityResult.reason);
      }
      
      console.log("Dashboard data loaded:", {
        engagement: engagementData.length,
        sentiment: sentimentData.length,
        tone: toneData.length,
        clarity: clarityData.length,
        network: Object.keys(networkData).length,
      });
      
      // Set the data
      setEngagementScoreData(engagementData);
      setSentimentScoreData(sentimentData);
      setToneScoreData(toneData);
      setClarityScoreData(clarityData);
      setNetworkMetrics(networkData);
      setNetworkGraph(networkGraphData); // Set network graph
      setNetworkDensityData(densitySeries);
      
      // Calculate index scores
      const collabIndex = calculateCollaborationHealthIndex(
        engagementData, 
        sentimentData, 
        toneData, 
        clarityData, 
        networkData
      );
      
      setCollaborationHealthIndex(collabIndex);
      
      // Generate chart data for indexes (computed per-day to show real variation)
      setCollaborationChartData(generateCollaborationChartData(engagementData, sentimentData, toneData, clarityData, networkData));
      
      // Calculate individual scores for digest table
      const avgEngagement = engagementData.length > 0 
        ? Math.round(engagementData.reduce((sum, item) => sum + (item.score || 0), 0) / engagementData.length)
        : 0;
      
      // Sentiment scores from generateSentimentTimeData are in -1 to 1 range, convert to 0-100
      console.log("Raw sentiment data sample:", sentimentData.slice(0, 3));
      const avgSentiment = sentimentData.length > 0 
        ? Math.round(sentimentData.reduce((sum, item) => {
            const score = item.score || 0;
            // Convert from -1 to 1 range to 0-100 percentage
            const percentage = ((score + 1) / 2) * 100;
            return sum + percentage;
          }, 0) / sentimentData.length)
        : 0;
      console.log("Calculated average sentiment:", avgSentiment);
      
      const avgTone = toneData.length > 0 
        ? Math.round(toneData.reduce((sum, item) => sum + (item.score || 0), 0) / toneData.length)
        : 0;
      const avgClarity = clarityData.length > 0 
        ? Math.round(clarityData.reduce((sum, item) => sum + (item.score || 0), 0) / clarityData.length)
        : 0;
      
      setEngagementScore(avgEngagement);
      setSentimentScore(avgSentiment);
      setToneScore(avgTone);
      setClarityScore(avgClarity);
      
      // Check if we have any data
      const hasData = engagementData.length > 0 || sentimentData.length > 0 || 
                     toneData.length > 0 || clarityData.length > 0;
      
      if (!hasData) {
        setNoDataAvailable(true);
        // Don't set error message for no data - the EmptyState component handles this
      } else {
        setNoDataAvailable(false);
      }
      
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      
      // Handle 404 (no report found) like other pages
      if (error.response && error.response.status === 404) {
        setNoDataAvailable(true);
        // Don't set error message for 404 - it just means no data, EmptyState component handles this
      } else {
        setError("Error loading dashboard data. Some metrics may be incomplete.");
      }
    } finally {
      setLoading(false);
      setDataLoading(false);
    }
  };
  
  // Handle date range change
  const handleDateRangeChange = useCallback((newDateRange) => {
    console.log("Date range changed:", newDateRange);
    
    if (!newDateRange || !newDateRange.startDate || !newDateRange.endDate) {
      console.warn("Invalid date range received:", newDateRange);
      return;
    }
    
    setDateRange(newDateRange);
  }, []);
  
  // Show loading indicator for the entire page during initial load
  if (loading && !initialLoadComplete) {
    return (
      <div className="flex h-screen h-dvh overflow-hidden bg-grey-10">
        <Sidebar user={user} organization={organization} />
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-grey-40 border-t-brand rounded-full animate-spin inline-block mb-4"></div>
            <p className="text-grey-60">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen h-dvh overflow-hidden bg-bg-gradient-dash">
      <Sidebar user={user} organization={organization} />
      
      <div className="flex-1  overflow-y-auto">
        {/* Header and filters */}
        <div className="p-5 pb-0">
          <h1 className="text-xl font-normal text-black-0">Dashboard</h1>
        </div>
        
        {/* Slack not connected message */}
        {!slackConnected && !loading && (
          <div className="bg-grey-20 p-4 rounded-md my-4">
            <p className="text-grey-60">
              You need to connect to Slack first. Please go to the{" "}
              <button 
                onClick={() => navigate('/settings')}
                className="text-brand hover:underline"
              >
                Settings page
              </button>{" "}
              to set up your Slack connection.
            </p>
          </div>
        )}
        
        {/* Error message */}
        {error && (
          <div className="bg-red-light p-4 rounded-md my-4">
            <p className="text-red-strong">{error}</p>
          </div>
        )}

        {/* No data available message */}
        {noDataAvailable && slackConnected && !loading && !dataLoading && (
          <>
          {/* Filters for no data state */}
          <div className="flex items-center justify-between p-5">
              <div className="flex items-center gap-3">
                <DateRangePicker 
                  dateRange={dateRange} 
                  onChange={handleDateRangeChange} 
                />
              </div>
            </div>

            <EmptyState
              variant="no-data"
              title="No data for the selected period"
              description="There are no Slack messages in the selected date range, or the data hasn't been analyzed yet. Try selecting a different time range or sync new data."
              actionButton={
                <SyncButtonWithTimestamp
                  organization={organization}
                  dateRange={dateRange}
                  onSyncComplete={handleSyncComplete}
                />
              }
            />
            
            
          </>
        )}
        
        {slackConnected && !noDataAvailable && (
          <>
            {/* Filters */}
            <div className="flex items-center justify-between p-5">
              <div className="flex items-center gap-3">
                <DateRangePicker 
                  dateRange={dateRange} 
                  onChange={handleDateRangeChange} 
                />
              </div>
              <SyncButtonWithTimestamp organization={organization} dateRange={dateRange} onSyncComplete={handleSyncComplete} />
            </div>
            
            {/* Loading indicator for filter changes */}
            {dataLoading && (
              <div className="flex justify-center items-center py-4 mb-4">
                <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin mr-3"></div>
                <span className="text-grey-60">Updating data...</span>
              </div>
            )}
            
            {/* Charts row */}
            <div className="flex gap-4 p-5 pt-0">
              <div className="flex-1">
                <IndexChart 
                  title="Collaboration health index"
                  data={collaborationChartData}
                  score={collaborationHealthIndex}
                  loading={dataLoading}
                  height={360}
                />
              </div>
              <div className="flex-1">
                <TeamworkTemperatureChart 
                  data={networkGraph.nodes}
                  loading={dataLoading}
                  height={360}
                  memberMap={memberMap}
                />
              </div>
              <div className="w-[360px]">
                <AISummaryWidget
                  title="AI insights summary"
                  text={aiSummary}
                  loading={aiSummaryLoading || dataLoading}
                />
              </div>
            </div>
            
            {/* Bottom row */}
            <div className="grid grid-cols-2 gap-4 p-5 pt-0">
              <DashboardWidget onViewMore={() => navigate("/network-analysis")}>
                <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle h-[360px] flex flex-col">
                  <div className="flex items-center gap-1 mb-3">
                    <h3 className="text-sm font-normal text-black-0">Network density</h3>
                    <TooltipUI
                      content="Network density measures the percentage of potential connections that are actually realized in your organization’s network."
                      placement="bottom"
                      align="start"
                    >
                      <span className="inline-flex cursor-help">
                        <InfoIcon />
                      </span>
                    </TooltipUI>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <p className="font-medium text-[18px] text-black-0">
                      {typeof networkMetrics.networkDensity === "number" ? Math.round(networkMetrics.networkDensity) : 0}%
                    </p>
                    <span className={`px-2 py-1 rounded-md text-xs font-normal ${getIndexBadgeColor(getMetricLevel(typeof networkMetrics.networkDensity === "number" ? Math.round(networkMetrics.networkDensity) : 0))}`}>
                      {getMetricLevel(typeof networkMetrics.networkDensity === "number" ? Math.round(networkMetrics.networkDensity) : 0)}
                    </span>
                  </div>
                  <div className="flex-1 min-h-0">
                    {networkDensityData && networkDensityData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={networkDensityData} margin={{ top: 10, right: 10, left: -20, bottom: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
                          <XAxis dataKey="date" tickFormatter={formatIsoDateShort} tick={{ fontSize: 12 }} stroke="#ABABBA" />
                          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} stroke="#ABABBA" />
                          <Tooltip content={<NetworkDensityChartTooltip />} />
                          <Line type="linear" dataKey="value" stroke="#31048B" strokeWidth={3} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-sm text-grey-60">No data</p>
                      </div>
                    )}
                  </div>
                </div>
              </DashboardWidget>

              <DashboardWidget onViewMore={() => navigate("/engagement")}>
                <EngagementScoreChart data={engagementScoreData} height={360} />
              </DashboardWidget>

              <SentimentScoreChart
                data={sentimentScoreData}
                height={360}
                onViewMore={() => navigate("/sentiment-analysis")}
              />

              <DashboardWidget onViewMore={() => navigate("/clarity")}>
                <ClarityScoreChart data={clarityScoreData} height={360} />
              </DashboardWidget>
            </div>
          </>
        )}
        
        {/* Empty dashboard when not connected */}
        {!slackConnected && !loading && !noDataAvailable && (
          <EmptyState
            variant="no-connection"
            title="Slack Not Connected"
            description="Connect your Slack workspace to view dashboard metrics and start analyzing your team's communication patterns."
            actionButton={
              <button 
                onClick={() => navigate("/settings")}
                className="bg-brand text-white px-6 py-2 rounded-md hover:bg-brand-dark transition-colors font-medium"
              >
                Connect Slack
              </button>
            }
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
